import { useState } from "react";
import { Link } from "react-router-dom";
import { AuthModal } from "@/components/AuthForms";
import { useBranding } from "@/hooks/useBranding";

const LINK_CLASS = "text-sm text-muted-foreground hover:text-foreground transition-colors";

export default function Footer({ platformName }) {
  const branding = useBranding();
  const name = platformName || branding.product_name;
  const logoUrl = branding.logo_url || "/favicon.png";
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  return (
    <footer className="border-t border-border bg-card">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 font-display font-bold text-lg text-foreground">
              <img src={logoUrl} alt="" className="h-6 w-6 rounded object-cover" />
              {name}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {branding.footer_text || "Find care near you."}
            </p>
            <div className="flex gap-4 mt-2">
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-muted-foreground hover:text-primary transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-muted-foreground hover:text-primary transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Twitter / X" className="text-muted-foreground hover:text-primary transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-muted-foreground hover:text-primary transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>

          {/* For Patients */}
          <div>
            <h3 className="font-display font-bold text-foreground text-sm mb-4 uppercase tracking-[0.12em]">For Patients</h3>
            <ul className="space-y-3">
              <li><Link to="/doctors" className={LINK_CLASS}>Browse doctors</Link></li>
              <li><Link to="/doctors" className={LINK_CLASS}>Book a token</Link></li>
              <li><button type="button" onClick={() => openAuth("login")} className={LINK_CLASS}>Sign in</button></li>
              <li><button type="button" onClick={() => openAuth("register")} className={LINK_CLASS}>Create account</button></li>
            </ul>
          </div>

          {/* For Doctors */}
          <div>
            <h3 className="font-display font-bold text-foreground text-sm mb-4 uppercase tracking-[0.12em]">For Doctors</h3>
            <ul className="space-y-3">
              <li><Link to="/apply" className={LINK_CLASS}>Apply as doctor</Link></li>
              <li><button type="button" onClick={() => openAuth("login")} className={LINK_CLASS}>Doctor sign in</button></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-display font-bold text-foreground text-sm mb-4 uppercase tracking-[0.12em]">Company</h3>
            <ul className="space-y-3">
              <li><Link to="/about" className={LINK_CLASS}>About us</Link></li>
              <li><Link to="/careers" className={LINK_CLASS}>Careers</Link></li>
              <li><Link to="/contact" className={LINK_CLASS}>Contact</Link></li>
              <li><Link to="/privacy" className={LINK_CLASS}>Privacy policy</Link></li>
              <li><Link to="/terms" className={LINK_CLASS}>Terms of service</Link></li>
            </ul>
          </div>
        </div>

        {/* App store + copyright */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="relative inline-block">
              <a href="#" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-black text-white px-4 py-2.5 rounded-xl text-xs font-semibold hover:bg-gray-800 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.626a1 1 0 010 1.732l-2.807 1.626L15.206 12l2.492-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>
                Google Play
              </a>
              <span className="absolute -top-2 -right-2 bg-[#a55e43] text-white text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full">Coming soon</span>
            </span>
            <span className="relative inline-block">
              <a href="#" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-black text-white px-4 py-2.5 rounded-xl text-xs font-semibold hover:bg-gray-800 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                App Store
              </a>
              <span className="absolute -top-2 -right-2 bg-[#a55e43] text-white text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full">Coming soon</span>
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} {name}. {branding.tagline || "Find care near you"}.
          </div>
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
    </footer>
  );
}
